import { GridMenuItem } from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { useTableApiContext } from '@/global/components/DataGrid/utils/table-context'
import { useTransmit } from '@/hooks/useTransmit'
import { CircularProgress } from '@mui/material'
import { AxiosError } from 'axios'
import { FileArchiveIcon, FileUserIcon } from 'lucide-react'
import { espelhoPonto } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { useCalculoStore } from './store'
import { EventEspelhoPdfMessage } from './types'
import { downloadPdf } from './utils'

export default function BaixarCalculoButton({
    competencia,
    formato,
}: {
    competencia: string
    formato: 'pdf' | 'zip'
}) {
    const empresaId = useStore(store => store.companyId)
    const state = useCalculoStore(store => store.state.state)
    const dispatch = useCalculoStore(store => store.dispatch)

    const { selectionModel, table } = useTableApiContext()

    const getIds = () =>
        (
            table?.getSortedRowModel().flatRows.map(row => row.original.id) ??
            []
        ).filter(id => selectionModel.indexOf(id) !== -1)

    const subscribe = useTransmit<EventEspelhoPdfMessage>({
        onMessage: async ({ status, data }, unsubscribe) => {
            switch (status) {
                case 'PROCESSANDO':
                    if (useCalculoStore.getState().state.progresso === null) {
                        toast.info(
                            'Estamos baixando os espelhos de ponto. Você será notificado quando estiver pronto. Fique à vontade para utilizar o sistema normalmente.',
                            {
                                duration: 5 * 1000,
                            },
                        )
                    }

                    dispatch({ action: 'PROGRESSO', data: data.progresso })

                    break
                case 'ERRO':
                    toast.error(data.message)
                    await unsubscribe()
                    dispatch(
                        { action: 'PROGRESSO', data: null },
                        { action: 'PROCESSANDO', data: false },
                    )
                    break
                case 'FINALIZADO':
                    if (data.pdf) downloadPdf(data.pdf)
                    if (data.zip) {
                        await espelhoPonto.calculos.file(`zip/${data.zip}`)
                    }

                    await unsubscribe()
                    dispatch(
                        { action: 'PROGRESSO', data: null },
                        { action: 'PROCESSANDO', data: false },
                    )
                    break
            }
        },
    })

    const handleClick = async () => {
        const ids = getIds()

        if (ids.length === 0) {
            toast.warning('Selecione pelo menos um espelho')
            return
        }

        dispatch({ action: 'PROCESSANDO', data: true })

        if (formato === 'zip') {
            toast.info(
                `O arquivo está sendo gerado com ${ids.length} espelhos, isso pode demorar um pouco...`,
            )
        }

        try {
            if (formato === 'pdf') {
                const channel = await espelhoPonto.calculos.post<
                    number[],
                    string
                >(`pdf?empresaId=${empresaId}`, ids)

                await subscribe(channel)
            } else {
                const channel: string = await espelhoPonto.calculos.zip({
                    data: ids,
                    isBlob: false,
                    queryParam: {
                        competencia,
                        empresaId,
                    },
                })

                await subscribe(channel)
            }
        } catch (error) {
            dispatch({ action: 'PROCESSANDO', data: false })
            const axiosError = error as AxiosError

            if (axiosError.message) {
                toast.error(axiosError.message)
            } else {
                toast.error('Erro ao gerar arquivo')
            }
        }
    }

    return (
        <GridMenuItem
            startIcon={(() => {
                if (state !== 'idle') return <CircularProgress size={24} />
                if (formato === 'zip')
                    return <FileArchiveIcon className="mr-2" />
                return <FileUserIcon className="mr-2" />
            })()}
            label={
                formato === 'zip'
                    ? 'Exportar selecionados como ZIP'
                    : 'Exportar selecionados como PDF'
            }
            onClick={handleClick}
            disabled={selectionModel.length === 0 || state !== 'idle'}
        />
    )
}
